// RichSelect.jsx

import React, { useEffect, useMemo, useRef, useState, forwardRef } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Popup from 'components/popup/Popup';
import PopupOption from 'components/popup/option/PopupOption';
import TextInput from 'components/textinput/TextInput';

// CSS
import './RichSelect.css';

/**
 * RichSelect
 *
 * Custom select input that renders options in a popup and supports rich option content.
 *
 * Option shape:
 * - value: string | number
 * - label: string
 * - iconKey?: string
 * - note?: string
 * - disabled?: boolean 
 */
const RichSelect = forwardRef(({
  value,
  onChange,
  onValueChange,
  options = [],
  placeholder = 'Select...',
  helperText = '',
  error = false,
  disabled = false,
  style = {},
  className = '',
  hybrid = false,
  iconSize = 22,
  startIconKey,
  endIconKey = 'keyboard_arrow_down',
  popupZIndex = 2000,
  emptyText = 'No options available',
  ...rest
}, ref) => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [isOpen, setOpen] = useState(false);
  const [popupWidth, setPopupWidth] = useState(null);

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  const normalizedOptions = useMemo(() => (
    (Array.isArray(options) ? options : []).map((option, index) => {
      const derivedValue = option?.value ?? option?.key ?? option?.id ?? '';
      const normalizedValue = String(derivedValue);
      const normalizedLabel = String(option?.label ?? '');

      return {
        ...option,
        value: normalizedValue,
        label: normalizedLabel,
        iconKey: option?.iconKey || '',
        note: option?.note || '',
        disabled: Boolean(option?.disabled),
        _listKey: normalizedValue || `${normalizedLabel || '__empty__'}_${index}`,
      };
    })
  ), [options]);

  const selectedValue = useMemo(() => (
    value == null || value === '' ? '' : String(value)
  ), [value]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === selectedValue) || null,
    [normalizedOptions, selectedValue]
  );

  useEffect(() => {
    if (!isOpen || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    if (rect?.width) setPopupWidth(rect.width);
  }, [isOpen, normalizedOptions.length, selectedValue]);

  const emitChange = (nextValue, option) => {
    const eventLike = {
      target: {
        value: nextValue,
        name: rest.name,
      },
      currentTarget: {
        value: nextValue,
        name: rest.name,
      },
    };

    onChange && onChange(eventLike);
    onValueChange && onValueChange(nextValue, option);
  };

  const handleSelect = (option) => {
    if (!option || option.disabled) return;
    emitChange(option.value, option);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = selectedOption?.label || '';
  const triggerStartIconKey = selectedOption?.iconKey || startIconKey;

  return (
    <div className={`rich-select-container ${className}`.trim()}>
      <div ref={anchorRef} className="rich-select-trigger">
        <TextInput
          ref={ref}
          value={displayValue}
          readOnly
          disabled={disabled}
          placeholder={placeholder}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
          startIconKey={triggerStartIconKey}
          startIconColor={theme.foregroundMuted}
          endIconKey={endIconKey}
          endIconColor={theme.foregroundMuted}
          iconSize={iconSize}
          helperText={helperText}
          error={error}
          hybrid={hybrid}
          style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={rest.id ? `${rest.id}-listbox` : undefined}
          {...rest}
        />
      </div>

      <Popup anchorRef={anchorRef} isOpen={isOpen} setOpen={setOpen} zIndex={popupZIndex}>
        <div
          id={rest.id ? `${rest.id}-listbox` : undefined}
          role="listbox"
          className="rich-select-popup"
          style={{
            backgroundColor: theme.backgroundMuted,
            color: theme.foreground,
            minWidth: isMobile ? "100%" : popupWidth ? `${popupWidth}px` : undefined,
            maxHeight: isMobile ? "60vh" : "300px",
          }}
        >
          {normalizedOptions.length === 0 && (
            <div className="rich-select-empty" style={{ color: theme.foregroundMuted }}>
              {emptyText}
            </div>
          )}

          {normalizedOptions.map((option, index) => {
            const isSelected = option.value === selectedValue;
            return (
              <PopupOption
                key={`${option._listKey}_${index}`}
                iconKey={option.iconKey || undefined}
                label={option.label}
                note={option.note || undefined}
                selected={isSelected}
                disabled={option.disabled}
                onClick={() => handleSelect(option)}
              />
            );
          })}
        </div>
      </Popup>
    </div>
  );
});

RichSelect.displayName = 'RichSelect';

export default RichSelect;
